import { translate, type MessageKey, type PoiCategory } from "@festipal/shared";
import {
  LngLatBounds,
  Map as MapLibreMap,
  Marker,
  NavigationControl,
} from "maplibre-gl";
import { useCallback, useEffect, useRef, useState } from "react";
import "maplibre-gl/dist/maplibre-gl.css";

import { festivals } from "./api";
import { loadBasemap } from "./basemap";

/**
 * Modération des relevés d'arpentage.
 *
 * Rien de ce qui est relevé sur le terrain n'atteint l'application sans passer
 * par ici. C'est la contrepartie du niveau B : on peut référencer un festival
 * sans son organisateur, à condition qu'un humain relise.
 *
 * La carte est là pour trancher l'essentiel : un point posé au milieu d'un champ
 * voisin se voit d'un coup d'œil, alors qu'une liste de coordonnées ne dit rien.
 */

type Contribution = {
  id: string;
  category: string;
  name: string;
  locale: string;
  comment: string | null;
  latitude: number;
  longitude: number;
  accuracyM: number;
  sampleCount: number;
  capturedAt: string;
  status: string;
  hasPhoto: boolean;
  author: { id: string; displayName: string };
};

type Status = "PENDING" | "ACCEPTED" | "REJECTED";

const STATUS_LABELS: Record<Status, string> = {
  PENDING: "En attente",
  ACCEPTED: "Retenus",
  REJECTED: "Écartés",
};

export function ContributionsEditor({ slug }: { slug: string }) {
  const container = useRef<HTMLDivElement | null>(null);
  const map = useRef<MapLibreMap | null>(null);
  const markers = useRef<Marker[]>([]);

  const [status, setStatus] = useState<Status>("PENDING");
  const [items, setItems] = useState<Contribution[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mapReady, setMapReady] = useState(false);

  const reload = useCallback(async () => {
    try {
      const result = await festivals.contributions(slug, status);
      setItems(result.contributions);
      setError(null);
    } catch {
      setError("Lecture impossible.");
    }
  }, [slug, status]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    let instance: MapLibreMap | null = null;
    let abandoned = false;

    void loadBasemap().then((basemap) => {
      if (abandoned || !container.current) return;
      instance = new MapLibreMap({
        container: container.current,
        style: basemap.style,
        center: basemap.center,
        zoom: basemap.zoom,
      });
      instance.addControl(new NavigationControl(), "top-right");
      map.current = instance;
      setMapReady(true);
    });

    return () => {
      abandoned = true;
      instance?.remove();
      map.current = null;
      setMapReady(false);
    };
  }, []);

  useEffect(() => {
    const instance = map.current;
    if (!instance) return;

    for (const marker of markers.current) marker.remove();
    markers.current = items.map((item) => {
      const dot = document.createElement("button");
      dot.type = "button";
      dot.setAttribute("aria-label", item.name);
      dot.style.cssText = `width:20px;height:20px;border-radius:50%;border:2px solid #fff;
        cursor:pointer;background:${item.id === selected ? "#e8590c" : "#0b7285"}`;
      dot.addEventListener("click", () => setSelected(item.id));
      return new Marker({ element: dot })
        .setLngLat([item.longitude, item.latitude])
        .addTo(instance);
    });

    if (items.length > 0) {
      const bounds = new LngLatBounds();
      for (const item of items) bounds.extend([item.longitude, item.latitude]);
      instance.fitBounds(bounds, { padding: 60, maxZoom: 17, duration: 0 });
    }
  }, [items, selected, mapReady]);

  const review = async (id: string, decision: "accept" | "reject") => {
    setBusy(id);
    try {
      await festivals.reviewContribution(slug, id, decision);
      setSelected(null);
      await reload();
    } catch {
      setError("Décision non enregistrée.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="card">
      <h2>Relevés du terrain</h2>
      <p className="muted">
        Chaque relevé vient de l'application, avec sa précision et le nombre de mesures
        qui l'ont produit. Retenir un relevé crée le lieu correspondant, déjà vérifié.
      </p>

      <div className="row">
        {(["PENDING", "ACCEPTED", "REJECTED"] as const).map((value) => (
          <button
            key={value}
            onClick={() => setStatus(value)}
            className={status === value ? "tab active" : "tab"}
            aria-pressed={status === value}
          >
            {STATUS_LABELS[value]}
          </button>
        ))}
        <span className="badge">{items.length}</span>
      </div>

      {error ? <p className="muted">{error}</p> : null}

      <div className="grid-2">
        <div>
          {items.length === 0 ? (
            <p className="muted">Rien à relire.</p>
          ) : (
            items.map((item) => (
              <article
                key={item.id}
                className={
                  item.id === selected ? "contribution selected" : "contribution"
                }
                onClick={() => setSelected(item.id)}
              >
                <h3>{item.name}</h3>
                <p className="muted">
                  {translate(
                    "fr",
                    `poi.category.${item.category as PoiCategory}` as MessageKey,
                  )}{" "}
                  · {item.author.displayName} ·{" "}
                  {new Date(item.capturedAt).toLocaleString("fr-FR")}
                </p>
                <p className="muted">
                  Précision {item.accuracyM.toFixed(1)} m sur {item.sampleCount} mesures
                  {/* Au-delà de quinze mètres, le point mérite un regard : il peut
                      tomber du mauvais côté d'une barrière. */}
                  {item.accuracyM > 15 ? " — à vérifier" : ""}
                </p>
                {item.comment ? <p>{item.comment}</p> : null}

                {item.hasPhoto ? (
                  <img
                    src={festivals.contributionPhotoUrl(slug, item.id)}
                    alt={`Photo du relevé « ${item.name} »`}
                    className="contribution-photo"
                  />
                ) : null}

                {item.status === "PENDING" ? (
                  <div className="row">
                    <button
                      className="primary"
                      onClick={() => void review(item.id, "accept")}
                      disabled={busy === item.id}
                    >
                      Retenir
                    </button>
                    <button
                      className="danger"
                      onClick={() => void review(item.id, "reject")}
                      disabled={busy === item.id}
                    >
                      Écarter
                    </button>
                  </div>
                ) : null}
              </article>
            ))
          )}
        </div>

        <div>
          <div className="map" style={{ height: 420 }} ref={container} />
        </div>
      </div>
    </section>
  );
}
